'use client'
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from 'react'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Gift,
  LockKeyhole,
  Send,
  Sparkles,
  Star,
} from 'lucide-react'
import {
  FeedbackError,
  validateAnswers,
  visibleQuestions,
  type Answers,
  type FeedbackForm,
  type FeedbackQuestion,
  type Answer,
} from './contract'
import './forms.css'

export type ExperienceForm = Pick<
  FeedbackForm,
  | 'title'
  | 'description'
  | 'confirmation'
  | 'questions'
  | 'design'
  | 'event_title'
  | 'award_points'
  | 'reward_kind'
  | 'reward_label'
  | 'closes_at'
>
export function FormExperience({
  form,
  onSubmit,
  submitted = false,
  pointAwarded = false,
  reward,
  preview = false,
  initialAnswers = {},
  playerName,
  draftKey,
}: {
  form: ExperienceForm
  onSubmit?: (answers: Answers) => Promise<void>
  submitted?: boolean
  pointAwarded?: boolean
  reward?: {
    kind: 'award' | 'prefix'
    label: string
    status: 'pending' | 'granted'
  } | null
  preview?: boolean
  initialAnswers?: Answers
  playerName?: string
  draftKey?: string
}) {
  const [answers, setAnswers] = useState<Answers>(initialAnswers)
  const [step, setStep] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [restored, setRestored] = useState(false)
  const [previewDone, setPreviewDone] = useState(false)
  const [errorQuestion, setErrorQuestion] = useState<string>()
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!draftKey || submitted) return
    try {
      const value = sessionStorage.getItem(draftKey)
      if (value) {
        setAnswers(JSON.parse(value))
        setRestored(true)
      }
    } catch {
      /* storage is optional */
    }
  }, [draftKey, submitted])
  useEffect(() => {
    if (!draftKey) return
    try {
      if (submitted) sessionStorage.removeItem(draftKey)
      else if (Object.keys(answers).length)
        sessionStorage.setItem(draftKey, JSON.stringify(answers))
    } catch {
      /* private browsing */
    }
  }, [answers, draftKey, submitted])
  const visible = visibleQuestions(form.questions, answers)
  const sections: FeedbackQuestion[][] = [[]]
  for (const q of visible) {
    if (q.type === 'section' && sections[sections.length - 1].length)
      sections.push([])
    sections[sections.length - 1].push(q)
  }
  const currentStep = Math.min(step, sections.length - 1),
    current = sections[currentStep]
  const answerable = visible.filter((q) => q.type !== 'section')
  const answered = answerable.filter((q) => {
    const a = answers[q.id]
    return (
      a != null &&
      a !== '' &&
      (!Array.isArray(a) || a.length > 0) &&
      (typeof a !== 'object' || Array.isArray(a) || Object.keys(a).length > 0)
    )
  }).length
  const update = (id: string, value: Answer) => {
    setAnswers((a) => ({ ...a, [id]: value }))
    setError('')
    setErrorQuestion(undefined)
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    try {
      if (currentStep < sections.length - 1) {
        validateAnswers(
          form.questions.map((q) => ({
            ...q,
            required: q.required && current.some((x) => x.id === q.id),
          })),
          answers
        )
        setStep(currentStep + 1)
        return
      }
      const clean = validateAnswers(form.questions, answers)
      if (preview) {
        setPreviewDone(true)
        return
      }
      setBusy(true)
      await onSubmit?.(clean)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Please check your answers.'
      )
      if (err instanceof FeedbackError && err.questionId) {
        setErrorQuestion(err.questionId)
        const section = sections.findIndex((items) =>
          items.some((q) => q.id === err.questionId)
        )
        if (section >= 0) setStep(section)
        requestAnimationFrame(() => {
          const field = root.current?.querySelector(
            `[data-question="${err.questionId}"]`
          )
          field?.scrollIntoView({ block: 'center', behavior: 'smooth' })
          field
            ?.querySelector<HTMLElement>('input,textarea,select')
            ?.focus({ preventScroll: true })
        })
      }
    } finally {
      setBusy(false)
    }
  }
  const rgb = form.design.accent
    .slice(1)
    .match(/.{2}/g)
    ?.map((v) => parseInt(v, 16) / 255) || [1, 1, 1]
  const luminance = rgb
    .map((v) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4))
    .reduce((sum, v, i) => sum + v * [0.2126, 0.7152, 0.0722][i], 0)
  const style = {
    '--form-accent': form.design.accent,
    '--cf-button-ink': luminance > 0.179 ? '#101b17' : '#ffffff',
    fontFamily:
      form.design.font === 'serif'
        ? 'Georgia, serif'
        : 'CDUFormText, Arial, sans-serif',
  } as CSSProperties
  if (submitted || previewDone)
    return (
      <div className="cdu-form" style={style}>
        <section className="cf-success">
          <span className="cf-success-icon">
            <CheckCircle2 size={36} />
          </span>
          <p className="cf-eyebrow">RESPONSE RECEIVED</p>
          <h1>You’re part of what comes next.</h1>
          <p>{form.confirmation}</p>
          {reward && (
            <div className="cf-reward" role="status">
              <Gift size={19} />
              <strong>{reward.label}</strong>
              <span>
                {reward.status === 'pending'
                  ? 'Your response is saved. Your reward is on its way; we’ll keep trying automatically.'
                  : reward.kind === 'award'
                    ? 'Available in your Tesseract awards'
                    : 'Available on your linked Minecraft account'}
              </span>
            </div>
          )}
          {pointAwarded && (
            <div className="cf-reward">
              <Gift size={19} />
              <strong>+1 event point</strong>
              <span>Added to your player balance</span>
            </div>
          )}
          {form.award_points && !pointAwarded && !preview && (
            <p className="cf-muted">
              Your response is saved. The feedback point is awarded once per
              event.
            </p>
          )}
          {preview ? (
            <button
              type="button"
              className="cf-primary"
              onClick={() => setPreviewDone(false)}
            >
              Back to preview
            </button>
          ) : (
            <a href="/forms" className="cf-primary">
              Explore more forms <ArrowRight size={17} />
            </a>
          )}
          {!preview && (
            <details className="cf-receipt">
              <summary>Review your answers</summary>
              {answerable.map((q) => (
                <div key={q.id}>
                  <strong>{q.title}</strong>
                  <p>{formatAnswer(answers[q.id]) || 'Not answered'}</p>
                </div>
              ))}
            </details>
          )}
        </section>
      </div>
    )
  return (
    <div ref={root} className="cdu-form" style={style}>
      <header className={`cf-hero cf-hero-${form.design.banner}`}>
        <div className="cf-hero-content">
          <p className="cf-eyebrow">
            <Sparkles size={14} />{' '}
            {form.event_title
              ? 'AFTER THE ADVENTURE'
              : 'YOUR VOICE, OUR COMMUNITY'}
          </p>
          <h1>{form.title || 'Your form title'}</h1>
          <p className="cf-description">
            {form.description ||
              'A little of your time. A big part of what comes next.'}
          </p>
          <div className="cf-meta">
            <span>
              <LockKeyhole size={14} /> Sign-in + Minecraft link required
            </span>
            {form.award_points && (
              <span>
                <Gift size={14} /> Earn 1 event point
              </span>
            )}
            {['award', 'prefix'].includes(form.reward_kind) &&
              form.reward_label && (
                <span>
                  <Gift size={14} /> {form.reward_label}
                </span>
              )}
            <span>
              About {Math.max(1, Math.ceil(answerable.length * 0.4))} min
            </span>
          </div>
        </div>
      </header>
      <div className="cf-body">
        {preview && (
          <div className="cf-notice">
            Live preview · Try every question. Preview answers are never saved
            or rewarded.
          </div>
        )}
        <div className="cf-progress-label">
          <span>
            {playerName
              ? `Hi ${playerName}. Make yourself heard.`
              : 'A few thoughts can make a difference.'}
          </span>
          <span>
            {answered} / {answerable.length}
          </span>
        </div>
        <div
          className="cf-progress"
          role="progressbar"
          aria-label="Questions answered"
          aria-valuenow={answered}
          aria-valuemin={0}
          aria-valuemax={answerable.length}
        >
          <span
            style={{
              width: `${(answered / Math.max(1, answerable.length)) * 100}%`,
            }}
          />
        </div>
        {restored && (
          <p className="cf-muted" role="status">
            Your unfinished answers were restored in this tab.
          </p>
        )}
        <form onSubmit={submit} noValidate>
          <div className="cf-questions">
            {current.map((q) =>
              q.type === 'section' ? (
                <section className="cf-section" key={q.id}>
                  <p className="cf-eyebrow">
                    PART {currentStep + 1} OF {sections.length}
                  </p>
                  <h2>{q.title}</h2>
                  <p>{q.description}</p>
                </section>
              ) : (
                <Question
                  key={q.id}
                  invalid={errorQuestion === q.id}
                  question={q}
                  value={answers[q.id]}
                  onChange={(v) => update(q.id, v)}
                  index={answerable.findIndex((x) => x.id === q.id) + 1}
                />
              )
            )}
          </div>
          {error && (
            <div className="cf-error" role="alert" tabIndex={-1}>
              {error}
            </div>
          )}
          <div className="cf-footer">
            <p>
              <LockKeyhole size={14} /> Your answers are visible to authorised
              CDU staff.
              <br />
              Fields marked * are required.
            </p>
            <div className="cf-actions">
              {currentStep > 0 && (
                <button
                  type="button"
                  className="cf-secondary"
                  onClick={() => setStep(currentStep - 1)}
                >
                  <ArrowLeft size={16} /> Back
                </button>
              )}
              <button type="submit" className="cf-primary" disabled={busy}>
                {busy
                  ? 'Sending…'
                  : currentStep < sections.length - 1
                    ? 'Continue'
                    : preview
                      ? 'Try submission'
                      : 'Send my response'}
                {currentStep < sections.length - 1 ? (
                  <ArrowRight size={16} />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </div>
          </div>
          {form.closes_at && (
            <p className="cf-muted">
              Closes {new Date(form.closes_at).toLocaleString()}
            </p>
          )}
        </form>
      </div>
    </div>
  )
}
export function formatAnswer(answer: Answer | undefined): string {
  if (answer == null) return ''
  if (Array.isArray(answer)) return answer.join(', ')
  if (typeof answer === 'object')
    return Object.entries(answer)
      .map(([k, v]) => `${k}: ${v}`)
      .join(' · ')
  return String(answer)
}
function Question({
  question: q,
  value,
  onChange,
  index,
  invalid,
}: {
  question: FeedbackQuestion
  value: Answer | undefined
  onChange: (value: Answer) => void
  index: number
  invalid: boolean
}) {
  const id = `question-${q.id}`,
    text = typeof value === 'string' || typeof value === 'number' ? value : ''
  return (
    <fieldset
      data-question={q.id}
      aria-invalid={invalid}
      className="cf-question"
      aria-describedby={q.description ? `${id}-help` : undefined}
    >
      <legend>
        <span className="cf-number">{String(index).padStart(2, '0')}</span>
        <span>
          {q.title}
          {q.required && <span className="cf-required"> *</span>}
        </span>
      </legend>
      {q.description && (
        <p id={`${id}-help`} className="cf-help">
          {q.description}
        </p>
      )}
      {['short', 'paragraph', 'number', 'date', 'time'].includes(q.type) &&
        (q.type === 'paragraph' ? (
          <textarea
            aria-label={q.title}
            required={q.required}
            maxLength={10000}
            rows={4}
            value={text}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Tell us a little more…"
          />
        ) : (
          <input
            aria-label={q.title}
            required={q.required}
            type={q.type === 'short' ? 'text' : q.type}
            min={q.type === 'number' ? q.min : undefined}
            max={q.type === 'number' ? q.max : undefined}
            step={q.type === 'number' ? 'any' : undefined}
            maxLength={1000}
            value={text}
            onChange={(e) =>
              onChange(
                q.type === 'number' && e.target.value !== ''
                  ? Number(e.target.value)
                  : e.target.value
              )
            }
            placeholder={q.type === 'short' ? 'Your answer…' : undefined}
          />
        ))}
      {q.type === 'dropdown' && (
        <select
          aria-label={q.title}
          required={q.required}
          value={text}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Choose an option</option>
          {q.options.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      )}
      {['single', 'multiple'].includes(q.type) && (
        <div className="cf-options">
          {q.options.map((option) => {
            const checked =
              q.type === 'multiple'
                ? Array.isArray(value) && value.includes(option)
                : value === option
            return (
              <label
                className={`cf-option ${checked ? 'cf-selected' : ''}`}
                key={option}
              >
                <input
                  type={q.type === 'multiple' ? 'checkbox' : 'radio'}
                  name={id}
                  value={option}
                  checked={checked}
                  onChange={() =>
                    onChange(
                      q.type === 'multiple'
                        ? checked
                          ? (value as string[]).filter((x) => x !== option)
                          : [...(Array.isArray(value) ? value : []), option]
                        : option
                    )
                  }
                />
                <span>{option}</span>
                {checked && <Check size={16} />}
              </label>
            )
          })}
        </div>
      )}
      {['rating', 'scale'].includes(q.type) && (
        <>
          <div className="cf-scale" role="radiogroup" aria-label={q.title}>
            {Array.from(
              { length: Math.max(0, Math.min(11, q.max - q.min + 1)) },
              (_, i) => i + q.min
            ).map((n) => (
              <label
                className={`cf-scale-option ${value === n ? 'cf-selected' : ''}`}
                key={n}
              >
                <input
                  type="radio"
                  name={id}
                  value={n}
                  checked={value === n}
                  onChange={() => onChange(n)}
                />
                {q.type === 'rating' && (
                  <Star
                    size={20}
                    fill={
                      typeof value === 'number' && n <= value
                        ? 'currentColor'
                        : 'none'
                    }
                  />
                )}
                <span>{n}</span>
              </label>
            ))}
          </div>
          <div className="cf-scale-labels">
            <span>{q.min} · Lowest</span>
            <span>{q.max} · Highest</span>
          </div>
        </>
      )}
      {q.type === 'grid' && (
        <div className="cf-grid">
          {q.rows.map((row) => (
            <label key={row}>
              <span>{row}</span>
              <select
                aria-label={`${q.title}: ${row}`}
                value={
                  value && !Array.isArray(value) && typeof value === 'object'
                    ? value[row] || ''
                    : ''
                }
                onChange={(e) => {
                  const next = {
                    ...(value &&
                    !Array.isArray(value) &&
                    typeof value === 'object'
                      ? value
                      : {}),
                    [row]: e.target.value,
                  }
                  if (!e.target.value) delete next[row]
                  onChange(next)
                }}
              >
                <option value="">Choose an option</option>
                {q.options.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </label>
          ))}
        </div>
      )}
      {!q.required && value != null && value !== '' && (
        <button className="cf-clear" type="button" onClick={() => onChange('')}>
          Clear answer
        </button>
      )}
    </fieldset>
  )
}
